/**
 * YouTube hero banner displayed at the top of the home page.
 * Accepts any YouTube URL form (watch?v=, youtu.be, embed, shorts) and
 * converts it to an embeddable URL with autoplay + mute (required for
 * autoplay to work in modern browsers).
 */

export function extractYouTubeId(url) {
  if (!url) return null;
  const trimmed = String(url).trim();

  // Already an 11-character ID
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;

  const patterns = [
    /youtu\.be\/([A-Za-z0-9_-]{11})/,
    /[?&]v=([A-Za-z0-9_-]{11})/,
    /\/embed\/([A-Za-z0-9_-]{11})/,
    /\/shorts\/([A-Za-z0-9_-]{11})/,
    /\/v\/([A-Za-z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m) return m[1];
  }
  return null;
}

export default function YouTubeHero({ url, title }) {
  const id = extractYouTubeId(url);
  if (!id) return null;
  const embed = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=1&rel=0&modestbranding=1&playsinline=1`;
  return (
    <section className="border-b border-neutral-200 bg-black" data-testid="home-video-hero">
      <div className="max-w-[1400px] mx-auto px-0 sm:px-6 lg:px-10 py-0 sm:py-6">
        {title && (
          <div className="px-6 lg:px-0 pt-4 sm:pt-0 sm:pb-4">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#0033FF] font-bold">Featured</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tight text-white">{title}</h2>
          </div>
        )}
        <div className="relative aspect-video w-full bg-black overflow-hidden">
          <iframe
            src={embed}
            title={title || "Featured video"}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
}
